<template>
  <main :class="['app-root', { 'app-root--auth': isStandalonePage }]">
    <RouterView v-if="isStandalonePage" />

    <template v-else>
      <header class="app-topbar">
        <button class="app-topbar__brand" type="button" @click="goMenu">
          <img src="@/assets/bioa_logo.png" alt="BIOA" />
        </button>

        <div class="app-topbar__search">
          <input
            v-model="globalSearch"
            type="text"
            placeholder="메뉴 검색"
            @focus="searchFocused = true"
            @keyup.enter="goFirstResult"
          />
          <button type="button" @click="goFirstResult">검색</button>

          <div v-if="searchFocused && globalSearch && filteredMenus.length" class="app-topbar__results">
            <button
              v-for="menu in filteredMenus"
              :key="menu.path"
              type="button"
              @click="goPage(menu.path)"
            >
              <strong>{{ menu.title }}</strong>
              <span>{{ menu.group }}</span>
            </button>
          </div>
        </div>

        <nav class="app-topbar__quick" aria-label="빠른이동 메뉴">
          <button
            v-for="item in quickMenus"
            :key="item.title"
            type="button"
            :class="{ active: item.path && route.path === item.path }"
            @click="item.action === 'logout' ? logout() : goPage(item.path)"
          >
            <span class="quick-icon" aria-hidden="true">
              <img :src="item.icon" alt="" />
            </span>
            <strong>{{ item.title }}</strong>
          </button>
        </nav>
      </header>

      <section class="app-content">
        <RouterView />
      </section>
    </template>
  </main>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import menuIcon from '@/assets/quick-icons/quick-menu.svg';
import deskIcon from '@/assets/quick-icons/quick-desk.svg';
import calendarIcon from '@/assets/quick-icons/quick-calendar.svg';
import consultIcon from '@/assets/quick-icons/quick-consult.svg';
import photoIcon from '@/assets/quick-icons/quick-photo.svg';
import documentIcon from '@/assets/quick-icons/quick-document.svg';
import ediIcon from '@/assets/quick-icons/quick-edi.svg';
import statsIcon from '@/assets/quick-icons/quick-stats.svg';
import logoutIcon from '@/assets/quick-icons/quick-logout.svg';

type MenuLink = {
  title: string;
  group: string;
  path: string;
  keywords: string[];
};

type QuickMenu = {
  title: string;
  icon: string;
  path: string;
  action?: 'logout';
};

const route = useRoute();
const router = useRouter();
const globalSearch = ref('');
const searchFocused = ref(false);

const menus: MenuLink[] = [
  { title: '전체 메뉴', group: '메인', path: '/menu', keywords: ['메뉴', 'main'] },
  { title: '데스크', group: '데스크', path: '/desk', keywords: ['접수', '수납', '예약목록'] },
  { title: '사용자 권한관리', group: '환경설정', path: '/users-permissions', keywords: ['사용자', '권한', '계정'] },
  { title: '환자관리', group: '환자', path: '/patients', keywords: ['환자', '고객', '차트'] },
  { title: '고객관리', group: 'CRM', path: '/client-manage', keywords: ['고객', 'CRM', '리콜'] },
  { title: '예약', group: '예약', path: '/reservation', keywords: ['예약', '내원'] },
  { title: '캘린더', group: '예약', path: '/calendar', keywords: ['달력', '예약', '일정'] },
  { title: '접수', group: '데스크', path: '/reception', keywords: ['접수', '대기', '내원'] },
  { title: 'EMR', group: '진료', path: '/emr', keywords: ['전자차트', '진료', '차트'] },
  { title: '수납/결제', group: '수납', path: '/payments', keywords: ['결제', '수납', '미수금'] },
  { title: '보험청구', group: '보험', path: '/insurance-claims', keywords: ['보험', '청구', 'EDI'] },
  { title: '문서/증명서', group: '문서', path: '/documents', keywords: ['문서', '증명서', '영수증'] },
  { title: '상담 보드', group: '상담', path: '/counseling', keywords: ['상담', '사진'] },
  { title: '가격 책정', group: '경영', path: '/price', keywords: ['가격', '수가', '비용'] },
  { title: '통계', group: '경영', path: '/analytics', keywords: ['통계', '매출', '실적'] },
  { title: '알림/리콜', group: 'CRM', path: '/alerts', keywords: ['알림', '문자', '리콜', '메신저'] },
  { title: '코드마스터', group: '환경설정', path: '/codes', keywords: ['설정', '코드', '환경'] },
];

const quickMenus: QuickMenu[] = [
  { title: '전체 메뉴', icon: menuIcon, path: '/menu' },
  { title: '데스크', icon: deskIcon, path: '/desk' },
  { title: '예약', icon: calendarIcon, path: '/calendar' },
  { title: '환자 상담', icon: consultIcon, path: '/plan' },
  { title: '사진 보기', icon: photoIcon, path: '/monitor' },
  { title: '문서 발급', icon: documentIcon, path: '/documents' },
  { title: '청구/EDI', icon: ediIcon, path: '/insurance-claims' },
  { title: '경영/통계', icon: statsIcon, path: '/analytics' },
  { title: '로그아웃', icon: logoutIcon, path: '/login', action: 'logout' },
];

const isAuthPage = computed(() => route.path === '/login' || route.path === '/master');
const isMobilePage = computed(() => route.path === '/m' || route.path.startsWith('/m/'));
const isStandalonePage = computed(() => isAuthPage.value || isMobilePage.value);
const filteredMenus = computed(() => {
  const query = globalSearch.value.trim().toLowerCase();
  if (!query) return [];
  return menus
    .filter((menu) => [menu.title, menu.group, ...menu.keywords].join(' ').toLowerCase().includes(query))
    .slice(0, 8);
});

function goMenu() {
  router.push('/menu');
}

function goFirstResult() {
  const first = filteredMenus.value[0];
  if (first) goPage(first.path);
}

function goPage(path: string) {
  globalSearch.value = '';
  searchFocused.value = false;
  router.push(path);
}

function logout() {
  localStorage.removeItem('bioa-session');
  router.push('/login');
}
</script>

<style>
html,
body,
#app {
  width: 100%;
  height: 100%;
  margin: 0;
}

body {
  font-family: Arial, "Noto Sans KR", sans-serif;
}

button,
input,
select,
textarea {
  font: inherit;
}
</style>

<style scoped>
.app-root {
  height: 100vh;
  overflow: hidden;
  background: #f6f9fb;
  color: #20313c;
}

.app-root--auth {
  overflow: auto;
}

.app-topbar {
  position: relative;
  z-index: 20;
  height: 82px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 14px;
  border-bottom: 1px solid #cfd8df;
  background: #fff;
  box-sizing: border-box;
  box-shadow: 0 1px 0 rgba(35, 49, 61, .04);
}

.app-topbar__brand {
  height: 64px;
  display: flex;
  align-items: center;
  border: 0;
  background: transparent;
  padding: 0 6px;
  cursor: pointer;
  flex: 0 0 auto;
}

.app-topbar__brand img {
  width: 112px;
  height: 38px;
  object-fit: contain;
}

.app-topbar__search {
  position: relative;
  width: 300px;
  display: flex;
  align-items: center;
  flex: 0 0 300px;
}

.app-topbar__search input {
  width: 100%;
  height: 34px;
  border: 1px solid #b9c7cf;
  border-radius: 8px 0 0 8px;
  padding: 0 10px;
  font-size: 13px;
  box-sizing: border-box;
  min-width: 0;
}

.app-topbar__search button {
  height: 34px;
  border: 1px solid #146b78;
  border-left: 0;
  border-radius: 0 8px 8px 0;
  background: #146b78;
  color: #fff;
  font-weight: 900;
  padding: 0 12px;
  cursor: pointer;
  white-space: nowrap;
  flex: 0 0 auto;
}

.app-topbar__results {
  position: absolute;
  top: 40px;
  left: 0;
  width: min(460px, calc(100vw - 24px));
  background: #fff;
  border: 1px solid #cfdce2;
  border-radius: 8px;
  box-shadow: 0 16px 34px rgba(35, 49, 61, .16);
  overflow: hidden;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  padding: 6px;
  gap: 4px;
  box-sizing: border-box;
}

.app-topbar__results button {
  width: 100%;
  min-height: 42px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  border: 0;
  border-radius: 6px;
  background: #fff;
  color: #34404a;
  text-align: left;
  padding: 7px 9px;
  box-sizing: border-box;
  cursor: pointer;
}

.app-topbar__results button:hover {
  background: #f3f8fb;
}

.app-topbar__results strong,
.app-topbar__results span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.app-topbar__results span {
  color: #7f8c98;
  font-size: 12px;
}

.app-topbar__quick {
  margin-left: auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  min-width: 0;
  flex: 1 1 auto;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 3px 0;
}

.app-topbar__quick button {
  height: 70px;
  min-width: 86px;
  border: 0;
  background: transparent;
  color: #283848;
  display: grid;
  grid-template-rows: 46px 18px;
  align-items: center;
  justify-items: center;
  gap: 2px;
  padding: 2px 3px;
  box-sizing: border-box;
  cursor: pointer;
  flex: 0 0 auto;
  border-radius: 8px;
}

.app-topbar__quick button:hover,
.app-topbar__quick button.active {
  background: #eef6ff;
  box-shadow: inset 0 -3px 0 #146b78;
}

.app-topbar__quick strong {
  max-width: 84px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0;
}

.quick-icon {
  width: 64px;
  height: 46px;
  display: grid;
  place-items: center;
  overflow: hidden;
}

.quick-icon img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  display: block;
}

.app-content {
  height: calc(100vh - 82px);
  overflow: auto;
  min-height: 0;
  -webkit-overflow-scrolling: touch;
}

@media (max-width: 1260px) {
  .app-topbar {
    height: auto;
    min-height: 82px;
    flex-wrap: wrap;
    padding: 8px 10px;
  }

  .app-topbar__search {
    width: auto;
    flex: 1 1 360px;
  }

  .app-topbar__quick {
    order: 3;
    flex-basis: 100%;
    justify-content: flex-start;
    margin-left: 0;
  }

  .app-content {
    height: calc(100vh - 158px);
  }
}
</style>
